import type { ReactNode } from "react";
import { parseDesignerWork } from "../../utils/jobCard";

type EmpOption = { emp_name: string };

const wrapSelectClass =
  "w-full min-w-0 box-border rounded-xl border border-[#C9D4D6] bg-white px-1.5 py-1.5 pr-5 text-[14px] leading-snug text-[#4A4A48] outline-none";

const modelSelectDisplayClass =
  "w-full min-w-0 box-border rounded-xl border border-violet-300 bg-violet-100 px-1.5 py-1.5 pr-5 text-[14px] leading-snug text-violet-950 outline-none";

export function WrappingSelect({
  value,
  onChange,
  children,
  placeholder = "Select",
  title,
  className = "",
  displayClassName,
}: {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  placeholder?: string;
  title?: string;
  className?: string;
  displayClassName?: string;
}) {
  return (
    <div className={`relative w-full min-w-0 ${className}`}>
      <select
        value={value}
        title={title || value}
        onChange={(e) => onChange(e.target.value)}
        className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
      >
        {children}
      </select>
      <div
        className={`${displayClassName || wrapSelectClass} whitespace-normal break-words peer-focus:border-[#2fa9bf] peer-focus:ring-2 peer-focus:ring-[#2fa9bf]/20 ${
          value ? "" : "text-[#8A8A85]"
        }`}
      >
        {value || placeholder}
      </div>
      <span
        aria-hidden
        className="pointer-events-none absolute right-2 top-2.5 border-x-4 border-x-transparent border-t-[5px] border-t-[#8A8A85]"
      />
    </div>
  );
}

type EmployeeNameEditorProps = {
  value: string;
  options: EmpOption[];
  onChange: (value: string) => void;
  allowEmpty?: boolean;
  allowMultiple?: boolean;
  placeholder?: string;
  showUnits?: boolean;
  unitsByName?: Record<string, number>;
  onUnitsChange?: (units: Record<string, number>) => void;
  unitValue?: number;
  onUnitChange?: (units: number) => void;
  showModelPerson?: boolean;
  modelBy?: string;
  modelUnits?: number;
  onModelByChange?: (value: string) => void;
  onModelUnitsChange?: (units: number) => void;
  autoModelUnits?: number;
  autoOtherModelUnits?: number;
  modelNotesHint?: string;
  onWorkChange?: (work: Record<string, "normal" | "model">) => void;
};

const withCurrent = (list: EmpOption[], current: string) => {
  if (!current) return list;
  return list.some((emp) => emp.emp_name === current)
    ? list
    : [{ emp_name: current }, ...list];
};

const remapUnits = (
  units: Record<string, number>,
  oldName: string,
  newName: string,
): Record<string, number> => {
  const next = { ...units };
  const value = oldName ? next[oldName] ?? 0 : 0;
  if (oldName) delete next[oldName];
  if (newName) next[newName] = value;
  return next;
};

const unitsInputClass =
  "w-11 shrink-0 rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-1 py-1.5 text-[14px] text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20";

const UnitsInput = ({
  value,
  disabled,
  min = 0,
  onChange,
}: {
  value: number | undefined;
  disabled?: boolean;
  min?: number;
  onChange: (units: number) => void;
}) => (
  <input
    type="number"
    min={min}
    disabled={disabled}
    value={value ?? ""}
    onChange={(e) => onChange(Number(e.target.value) || 0)}
    className={unitsInputClass}
    title="Units"
  />
);

export const ModelPersonRow = ({
  name,
  options,
  onChange,
  units = 1,
  onUnitsChange,
  autoUnits,
  notesHint,
  min = 1,
}: {
  name: string;
  options: EmpOption[];
  onChange?: (value: string) => void;
  units?: number;
  onUnitsChange?: (units: number) => void;
  autoUnits?: number;
  notesHint?: string;
  min?: number;
}) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <div className="flex min-w-0 items-start gap-1">
      {onChange ? (
        <WrappingSelect
          value={name}
          onChange={onChange}
          placeholder="Model by"
          displayClassName={modelSelectDisplayClass}
        >
          <option value="">Model by</option>
          {withCurrent(options, name).map((emp) => (
            <option key={emp.emp_name} value={emp.emp_name}>
              {emp.emp_name}
            </option>
          ))}
        </WrappingSelect>
      ) : (
        <div
          className={`${modelSelectDisplayClass} whitespace-normal break-words`}
          title={name}
        >
          {name || "—"}
        </div>
      )}
      {onUnitsChange ? (
        <UnitsInput
          value={units}
          min={min}
          onChange={(next) => onUnitsChange(Math.max(next, min))}
        />
      ) : (
        <input
          type="number"
          min={min}
          readOnly
          value={units}
          className={`${unitsInputClass} cursor-not-allowed bg-violet-50 text-violet-900`}
          title="Model units"
        />
      )}
    </div>
    {onUnitsChange && autoUnits != null ? (
      <div className="text-[11px] leading-tight text-[#8A8A85]">
        Auto {autoUnits}
        {Number(units) !== Number(autoUnits) ? " · edited" : ""}
        {notesHint ? " · extra qty in notes is manual" : ""}
      </div>
    ) : null}
  </div>
);

const EmployeeNameEditor = ({
  value,
  options,
  onChange,
  allowEmpty = false,
  allowMultiple = true,
  placeholder = "Select",
  showUnits = false,
  unitsByName = {},
  onUnitsChange,
  unitValue,
  onUnitChange,
  showModelPerson = false,
  modelBy = "",
  modelUnits = 1,
  onModelByChange,
  onModelUnitsChange,
  autoModelUnits,
  autoOtherModelUnits = 0,
  modelNotesHint,
  onWorkChange,
}: EmployeeNameEditorProps) => {
  const names = String(value || "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
  const extraModelPeople = showModelPerson ? names.slice(1) : [];

  const syncExtras = (
    nextNames: string[],
    nextUnits?: Record<string, number>,
  ) => {
    onWorkChange?.(
      parseDesignerWork({}, nextNames, modelBy, showModelPerson),
    );
    if (showModelPerson && !modelBy && nextNames[0]) {
      onModelByChange?.(nextNames[0]);
    }
    if (nextUnits) onUnitsChange?.(nextUnits);
  };

  const updateAt = (index: number, nextName: string) => {
    const oldName = names[index] || "";
    const next = [...names];
    if (!nextName) {
      if (allowEmpty && next.length <= 1) {
        onChange("");
        syncExtras([], remapUnits(unitsByName, oldName, ""));
        return;
      }
      if (next.length > 1) {
        next.splice(index, 1);
        onChange(next.join(", "));
        syncExtras(next, remapUnits(unitsByName, oldName, ""));
      }
      return;
    }
    next[index] = nextName;
    onChange(next.join(", "));
    if (oldName !== nextName) {
      syncExtras(next, remapUnits(unitsByName, oldName, nextName));
    }
  };

  const removeAt = (index: number) => {
    if (names.length <= 1 && !allowEmpty) return;
    const oldName = names[index];
    const next = names.filter((_, i) => i !== index);
    onChange(next.join(", "));
    syncExtras(next, remapUnits(unitsByName, oldName, ""));
  };

  const addName = (nextName: string) => {
    if (!nextName || names.includes(nextName)) return;
    const next = [...names, nextName];
    const hadExtras = names.slice(1).length > 0;
    onChange(next.join(", "));
    const extraUnits = hadExtras
      ? 1
      : Math.max(Number(autoOtherModelUnits) || 0, 1);
    syncExtras(next, {
      ...unitsByName,
      [nextName]: showModelPerson ? extraUnits : 0,
    });
  };

  const unusedOptions = options.filter((emp) => !names.includes(emp.emp_name));
  const showPrimaryModelRow =
    showModelPerson &&
    (extraModelPeople.length === 0 ||
      Number(modelUnits) > 0 ||
      Number(autoModelUnits) > 0);
  const modelBlock = showPrimaryModelRow ? (
      <ModelPersonRow
        name={modelBy || names[0] || ""}
        options={options}
        onChange={onModelByChange}
        units={modelUnits}
        onUnitsChange={onModelUnitsChange}
        autoUnits={autoModelUnits}
        notesHint={modelNotesHint}
        min={extraModelPeople.length ? 0 : 1}
      />
    ) : null;

  if (!allowMultiple) {
    const current = names[0] || "";
    return (
      <div className="flex w-full min-w-0 flex-col gap-1">
        <div className="flex w-full min-w-0 items-start gap-1">
          <WrappingSelect
            value={current}
            onChange={onChange}
            placeholder={placeholder}
          >
            <option value="">{placeholder}</option>
            {withCurrent(options, current).map((emp) => (
              <option key={emp.emp_name} value={emp.emp_name}>
                {emp.emp_name}
              </option>
            ))}
          </WrappingSelect>
          {showUnits ? (
            <UnitsInput
              value={unitValue}
              disabled={!current}
              onChange={(units) => onUnitChange?.(units)}
            />
          ) : null}
        </div>
        {modelBlock}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-1">
      {(names.length ? names : [""]).map((name, index) => {
        const isModelRow = index > 0 && showModelPerson;
        return (
          <div key={`${name}-${index}`} className="flex min-w-0 items-start gap-1">
            <WrappingSelect
              value={name}
              onChange={(nextName) => updateAt(index, nextName)}
              placeholder={placeholder}
              displayClassName={isModelRow ? modelSelectDisplayClass : undefined}
            >
              <option value="">{placeholder}</option>
              {withCurrent(options, name).map((emp) => (
                <option key={emp.emp_name} value={emp.emp_name}>
                  {emp.emp_name}
                </option>
              ))}
            </WrappingSelect>
            {showUnits ? (
              <UnitsInput
                value={
                  name
                    ? isModelRow
                      ? Math.max(Number(unitsByName[name]) || 0, 1)
                      : unitsByName[name]
                    : undefined
                }
                min={isModelRow ? 1 : 0}
                disabled={!name}
                onChange={(units) => {
                  if (!name) return;
                  onUnitsChange?.({
                    ...unitsByName,
                    [name]: isModelRow ? Math.max(units, 1) : units,
                  });
                }}
              />
            ) : null}
            {names.length > 1 ? (
              <button
                type="button"
                onClick={() => removeAt(index)}
                className="h-6 w-6 rounded text-xs text-red-600 hover:bg-red-50"
                title="Remove"
              >
                ×
              </button>
            ) : allowEmpty && name ? (
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  syncExtras([], remapUnits(unitsByName, name, ""));
                }}
                className="h-6 w-6 rounded text-xs text-red-600 hover:bg-red-50"
                title="Remove"
              >
                ×
              </button>
            ) : null}
          </div>
        );
      })}
      {allowMultiple && unusedOptions.length > 0 ? (
        <select
          value=""
          onChange={(e) => addName(e.target.value)}
          className="min-w-0 w-full rounded-xl border border-dashed border-[#DDDDD8] bg-white px-1.5 py-1 text-[13px] text-[#8A8A85] outline-none"
        >
          <option value="">{showModelPerson ? "+ Add model person" : "+ Add"}</option>
          {unusedOptions.map((emp) => (
            <option key={emp.emp_name} value={emp.emp_name}>
              {emp.emp_name}
            </option>
          ))}
        </select>
      ) : null}
      {modelBlock}
    </div>
  );
};

export default EmployeeNameEditor;
